import React, { useRef } from 'react';
import html2pdf from "html2pdf.js";

export function AEPSReceiptModa({
  receiptData = null,
  isOpen = false,
  onClose = () => {},
  title = "Transaction Receipt",
  logoUrl = "/assets/images/logo/2.png",
}) {

  // Scoped to ONLY the printable invoice card — deliberately excludes the
  // footer buttons so Print/Download don't capture their own UI controls.
  const printableRef = useRef();

  const safe = (v, def = "N/A") =>
    v === null || v === undefined || v === "" ? def : v;

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "" || amount === "null") return "₹0.00";
    const n = parseFloat(amount);
    return Number.isNaN(n) ? "₹0.00" : `₹${n.toFixed(2)}`;
  };

  // Normalizes a status value from any provider into one of: success | failed | pending
  const normalizeStatus = (status) => {
    if (status === true) return "success";
    if (status === false) return "failed";
    const s = (status || "").toString().toLowerCase();
    if (s === "success" || s === "successful" || s === "txn") return "success";
    if (s === "failed" || s === "failure" || s === "error" || s === "err") return "failed";
    if (s === "pending" || s === "processing" || s === "tup" || s === "initiated") return "pending";
    return "unknown";
  };

  const getStatusClass = (status) => {
    const n = normalizeStatus(status);
    if (n === "success") return "text-success";
    if (n === "failed") return "text-danger";
    return "text-warning"; // pending / unknown
  };

  const getStatusLabel = (status) => {
    const n = normalizeStatus(status);
    if (n === "success") return "SUCCESS";
    if (n === "failed") return "FAILED";
    if (n === "pending") return "PENDING";
    return safe(status, "UNKNOWN").toString().toUpperCase();
  };

  const mapTransectionType = (type) => {
    switch ((type || "").toUpperCase()) {
      case "BE": return "Balance Enquiry";
      case "CW": return "Cash Withdrawal";
      case "CD": return "Cash Deposit";
      case "MS": return "Mini Statement";
      default: return safe(type);
    }
  };

  // ---- Mini-statement normalization -----------------------------------
  // Different AEPS providers (InstantPay, Paysprint, etc.) can use different
  // key names for the same fields. Rather than assume one shape, check every
  // plausible variant so an unfamiliar provider degrades to "N/A" per-field
  // instead of the whole row breaking or throwing.
  const pick = (obj, keys, def = "N/A") => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "") {
        return obj[k];
      }
    }
    return def;
  };

  const normalizeMiniStatementRow = (txn) => ({
    date: pick(txn, ["date", "txn_date", "Date", "transactionDate"]),
    txnType: pick(txn, ["txnType", "txn_type", "type", "Type"]),
    amount: pick(txn, ["amount", "Amount", "txnAmount"], 0),
    narration: pick(txn, ["narration", "Narration", "remarks", "description", "desc"]),
  });

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: "Transaction Receipt",
          text: "Here is your transaction receipt",
          url: window.location.href,
        })
        .catch(() => {});
    } else {
      showMainToastFallback("Sharing isn't supported on this device/browser");
    }
  };

  // Falls back to a plain alert only if the app-wide toast isn't available in
  // this scope — keeps this file droppable without an import for that case.
  const showMainToastFallback = (msg) => {
    try {
      // eslint-disable-next-line no-undef
      showMainToast(msg, "danger");
    } catch {
      alert(msg);
    }
  };

  const handlePrint = () => {
    if (!printableRef.current) return;
    const printContents = printableRef.current.innerHTML;
    const newWindow = window.open("", "_blank");
    if (!newWindow) {
      showMainToastFallback("Please allow popups to print the receipt");
      return;
    }
    newWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <link rel="stylesheet"
            href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"/>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    newWindow.document.close();
    // Give the stylesheet a moment to load before printing, otherwise some
    // browsers print with no styling at all.
    newWindow.onload = () => newWindow.print();
    setTimeout(() => newWindow.print(), 300);
  };

  const handleDownload = () => {
    if (!printableRef.current) return;
    const opt = {
      margin: 0.5,
      filename: "receipt.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
    html2pdf().set(opt).from(printableRef.current).save();
  };

  if (!isOpen) return null;

  const data = receiptData || {};

  // Header date/time: backend (ShowReceiptAEPS) returns `created_at`, not
  // `datetime` — read created_at first, keep datetime as a fallback in case
  // some other provider path does send that key.
  const headerDateTime = safe(data.created_at ?? data.datetime);

  const normalizedMiniStatement = Array.isArray(data.ministatement)
    ? data.ministatement.map(normalizeMiniStatementRow)
    : [];

  // AEPS FIELD MAPPING — matches ShowReceiptAEPS's actual response keys,
  // plus the two fields it returns but the old version never displayed.
  const tableData = [
    { label: "Transaction Type", value: mapTransectionType(data.transection_type) },
    { label: "Order ID", value: safe(data.order) },
    { label: "Bank Reference No. (RRN)", value: safe(data.bank_rrn) },
    { label: "Customer", value: safe(data.customer) },
    { label: "Aadhar", value: safe(data.aadhar_number) },
    { label: "Bank Name", value: safe(data.bank_name) },
    { label: "Amount", value: formatCurrency(data.amount) },
    { label: "Account Balance", value: data.balance != null ? formatCurrency(data.balance) : "N/A" },
    { label: "Message", value: safe(data.desc) },
    { label: "Date & Time", value: headerDateTime },
    {
      label: "Status",
      value: getStatusLabel(data.status),
      isStatus: true,
      rawStatus: data.status,
    },
  ];

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-fullscreen-md-down"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">

          <div className="modal-header">
            <h4 className="main-title">{title}</h4>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body p-0">
            {/* Everything the user should see printed/downloaded lives inside
                this ref. The footer action buttons sit OUTSIDE it. */}
            <div ref={printableRef}>
              <div className="container invoice-container">
                <div className="row">
                  <div className="col-12">

                    <div className="card">
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table">

                            {/* HEADER */}
                            <tbody>
                            <tr>
                              <td>
                                <table className="table table-lg w-100 invoice-header">
                                  <tbody>
                                    <tr>
                                      <td>
                                        <img src={logoUrl} alt="LOGO"
                                          style={{ width: "200px", height: "60px", objectFit: "contain" }} />
                                        <address>Customer Copy - AEPS Receipt</address>
                                      </td>

                                      <td>
                                        <div className="text-end">
                                          <h3 className="text-primary">RECEIPT</h3>
                                          <p><strong>{headerDateTime}</strong></p>
                                          <p>
                                            <strong className={getStatusClass(data.status)}>
                                              {getStatusLabel(data.status)}
                                            </strong>
                                          </p>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>

                            {/* MAIN TABLE */}
                            <tr>
                              <td>
                                <table className="table table-lg table-bottom-border w-100">
                                  <thead>
                                    <tr>
                                      <th>Description</th>
                                      <th>Details</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {tableData.map((row, i) => (
                                      <tr key={i}>
                                        <td style={{ fontWeight: "500" }}>{row.label}</td>
                                        <td>
                                          {row.isStatus ? (
                                            <span className={getStatusClass(row.rawStatus)}>
                                              <strong>{row.value}</strong>
                                            </span>
                                          ) : (
                                            row.value
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>

                            {/* MINI STATEMENT (field names normalized across providers) */}
                            {normalizedMiniStatement.length > 0 && (
                              <tr>
                                <td>
                                  <h5 className="text-center mb-3 fw-bold text-primary">
                                    Mini Statement
                                  </h5>

                                  <table className="table table-bordered text-center">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Date</th>
                                        <th>Txn Type</th>
                                        <th>Amount</th>
                                        <th>Narration</th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {normalizedMiniStatement.map((txn, idx) => (
                                        <tr key={idx}>
                                          <td>{txn.date}</td>
                                          <td>{txn.txnType}</td>
                                          <td>{formatCurrency(txn.amount)}</td>
                                          <td>{txn.narration}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}

                            {/* FOOTER NOTE */}
                            <tr>
                              <td>
                                <div className="text-center p-3 bg-light rounded">
                                  <strong>Note:</strong> This is a system generated receipt.
                                </div>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>

                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER BUTTONS — deliberately outside printableRef */}
            <div className="invoice-footer float-end mb-3 p-3">
              <button className="btn btn-primary m-1" onClick={handlePrint}>
                Print
              </button>
              <button className="btn btn-secondary m-1" onClick={handleShare}>
                Share
              </button>
              <button className="btn btn-success m-1" onClick={handleDownload}>
                Download
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}